import * as React from "react";
import "./DynProvisionProgressDlg.scss";
import Api from "./Api";
import Dialog from "./input/Dialog";
import { IDynProvision } from "@testout/testout-commerce/models/DynProvision";
import { EmailArray } from "@testout/testout-commerce/models/Email";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { User } from "@testout/testout-commerce/models/labsim/User";
import { Reservation, ReservationType } from "@testout/testout-commerce/models/labsim/Reservation";
import { DynContact } from "@testout/testout-commerce/models/dynamics/DynContact";
import { ContactName } from "@testout/testout-commerce/models/ContactName";

interface DynProvisionProgressProps {
    provision: IDynProvision;
    products: ListItem[],
    env?: string,
    isOpen: boolean;
    onClose: (failed: boolean) => void;
}

interface DynProvisionProgressState {
    percent: number;
    messages: string[];
    disableClose: boolean;
}

export default class DynProvisionProgressDlg extends React.PureComponent<DynProvisionProgressProps, DynProvisionProgressState> {
    constructor(props: DynProvisionProgressProps) {
        super(props);
        this.state = { messages: [], percent: 0, disableClose: true };
    }

    _scrollerRef = React.createRef<HTMLDivElement>();

    onOpen = async () => {
        const { provision, products } = this.props;

        try {
            this.setState({ messages: [], percent: 0, disableClose: true });

            // Determine the task size for progress bar
            const counts = provision.accounts.map(account => new EmailArray(account.emails).length);
            const taskPercent = 100 / (1 + counts.length + counts.reduce((sum, count) => sum + count, 0) * 10);
            let percent = 0;

            // Build the list of courses from the selected products
            const productIds: number[] = provision.products.map((productNumber: string) => parseInt(products.find((item) => item.value === productNumber)?.["data"]?.["courseId"] ?? "0" as string));

            // Provision each account
            for (const account of provision.accounts) {

                // ACCOUNT
                this.setState({ messages: [...this.state.messages, `Associating ${account.name} to CertMaster Institution.`] });

                // Ensure the account is linked to the selected campus
                await Api.patch(`/api/dynamics/accounts/${account.accountId}?env=${this.props.env || ""}`, { comptia_labsimcampusprofileid: account.campusProfileId });
                this.setState({ percent: percent += taskPercent });

                // INSTRUCTOR LICENSE
                if (provision.useInstructorLicense) {
                    this.setState({ messages: [...this.state.messages, ` Provisioning instructor licenses...`] });
                    const reservations = await Api.get<Reservation[]>(`/api/campuses/${account.campusProfileId}/reservations?reservationType=InstructorLicense&description=Instructor License`);
                    const reservation = reservations.find(r => !r.isDeleted && r.name === "Instructor License");
                    if (reservation) {
                        // Merge the selected courses with the existing ones and extend the expiration date if needed
                        reservation.productIds = [...new Set([...reservation.productIds, ...productIds])];
                        if (provision.expirationDate && reservation.expirationDate && reservation.expirationDate > provision.expirationDate)
                            reservation.expirationDate = provision.expirationDate;
                        await Api.put(`/api/reservations`, reservation);
                    } else
                        await Api.post<Reservation>(`/api/reservations`,
                            {
                                reservationType: ReservationType.InstructorLicense,
                                productIds: productIds,
                                expirationDate: provision.expirationDate,
                                isCampusRequired: true,
                                campusId: account.campusProfileId,
                                name: "Instructor License",
                                allowedActivationCount: 0
                            });
                }
                this.setState({ percent: percent += taskPercent });

                // Provision each email of the account
                for (const email of new EmailArray(account.emails)) {

                    try {

                        // CONTACT
                        this.setState({
                            messages: [...this.state.messages, `Setting up instructor ${email.toString()}.`]
                        });

                        // Try to find the contact by email address
                        let contact = await Api.get<DynContact>(`/api/dynamics/contacts?email=${encodeURIComponent(email.address)}&env=${this.props.env || ""}`);
                        this.setState({ percent: percent += taskPercent });

                        // Determine a first name and last name from the email (LabSim requires both parts)
                        let person = new ContactName(email.displayName || email.localPart);
                        if (!person.firstNames)
                            person.firstNames = (person.lastName?.length ?? 0) > 0 ? person.lastName?.[0] : "unknown";
                        if (!person.lastName)
                            person.lastName = "unknown"

                        // Create a new contact if we could not find one and link it to the account
                        if (!contact)
                            contact = await Api.post<DynContact>(`/api/dynamics/contacts?env=${this.props.env || ""}`,
                                {
                                    emailaddress1: email.address,
                                    fullname: person.toString(),
                                    //roles: "instructor",
                                    parentcustomerid: account.accountId
                                }) as DynContact;
                        this.setState({ percent: percent += taskPercent });

                        // Ensure the contact is linked to the deal if one was provided
                        if (provision.opportunityId)
                            await Api.put(`/api/dynamics/opportunities/${provision.opportunityId}/contacts/${contact.contactid}?env=${this.props.env || ""}`, "PUT");
                        this.setState({ percent: percent += taskPercent });


                        // USER
                        this.setState({ messages: [...this.state.messages, ` Provisioning user...`] });

                        // Try to locate an existing user for the contact
                        let user: User | undefined;
                        if (contact.comptia_labsimuserprofileid) {
                            try {
                                user = await Api.get<User>(`/api/users/${contact.comptia_labsimuserprofileid}`);
                            }
                            catch (userError: any)
                            {
                                if (userError.message?.includes("404 (Not Found)"))
                                    user = undefined;
                                else
                                    throw userError;
                            }
                            if (!user) contact.comptia_labsimuserprofileid = "";
                        }
                        this.setState({ percent: percent += taskPercent });

                        // Create a new user if we could not find one and link it to the contact
                        if (!contact.comptia_labsimuserprofileid) {
                            user = await Api.post<User>("/api/users",
                                {
                                    givenName:"",
                                    surname:"",
                                    signOns: [{ systemId: 0, email: email.address }],
                                    fullName: person.toString()
                                });
                            this.setState({ percent: percent += taskPercent });

                            if (!user)
                                throw new Error("User could not be created");
                            await Api.patch(`/api/dynamics/contacts/${contact.contactid}?env=${this.props.env || ""}`,
                                { comptia_labsimuserprofileid: user.userProfileId });
                            this.setState({ percent: percent += taskPercent });
                        } else
                            this.setState({ percent: percent += 2 * taskPercent });

                        if (!user)
                            throw new Error("User could not be found or created");

                        // Ensure the user is linked to the selected campus
                        await Api.post(`/api/users/${user.userProfileId}/campuses/${account.campusProfileId}?isInstructor=true&isAdmin=${account.makeTeachersAdmins}`);
                        this.setState({ percent: percent += taskPercent });


                        // ACCESS

                        // Make a reservation and activate it for the user
                        let reservation: Reservation | undefined;
                        if (!provision.useInstructorLicense) {
                            this.setState({ messages: [...this.state.messages, ` Provisioning CertMaster course access...`] });
                            reservation = await Api.post<Reservation>(`/api/reservations`,
                                {
                                    reservationType: ReservationType.ActivationCode,
                                    productIds: productIds,
                                    expirationDate: provision.expirationDate,
                                    isCampusRequired: true,
                                    campusId: account.campusProfileId,
                                    allowedActivationCount: 1
                                });
                            this.setState({ percent: percent += taskPercent });

                            await Api.post(`/api/users/${user.userProfileId}/activations/${reservation.reservationId}/1`);
                            this.setState({ percent: percent += taskPercent });
                        } else
                            this.setState({ percent: percent += 2 * taskPercent });


                        // Send a welcome email to the contact with their temporary credentials
                        this.setState({ messages: [...this.state.messages, ` Sending welcome email...`] });
                        const productList = provision.products.map(productNumber =>
                            products.find(item => item.value === productNumber)?.["name"] ?? "");

                        await Api.post(`/api/dynamics/contacts/${contact.contactid}/welcome?env=${this.props.env || ""}`,
                            {
                                userProfileId: user.userProfileId,
                                campusProfileId: account.campusProfileId,
                                reservationId: reservation?.reservationId,
                                accountId: account.accountId,
                                email: contact.emailaddress1,
                                firstname: contact.firstname,
                                lastname: contact.lastname,
                                login: (user.signOns ?? []).find(signOn => signOn.systemId === "0")?.["userKey"],
                                password: (user.signOns ?? []).find(signOn => signOn.systemId === "0")?.["tempPassword"],
                                productlist: this.formatList(productList)
                            });
                        this.setState({ percent: percent += taskPercent });

                    }
                    catch (error: any) {
                        this.setState({ messages: [...this.state.messages, `*${error.message}`] });
                    }
                }
            }
            this.setState({ disableClose: false, messages: [...this.state.messages, `Provisioning complete.`] });
        }
        catch (err: any) {
            this.setState({ disableClose: false, messages: [...this.state.messages, `*${err.message}`] });
        }
    }

    onClose = () => {
        this.props.onClose(!!this.state.messages.find(message => message.startsWith("*")));
    }
    
    private formatList(n: string[]): string {
        return n.length === 0 ? '' : n.length === 1 ? n[0] 
            : n.length === 2 ? n.join(" and ") : `${n.slice(0, -1).join(", ")}, and ${n.slice(-1)}`;
    }

    scrollToBottom() {
        if (this.props.isOpen && this._scrollerRef.current) {
            const divScroller = this._scrollerRef.current as HTMLDivElement;
            divScroller.scrollTop = divScroller.scrollHeight;
        }
    }

    componentDidMount() {
        this.scrollToBottom();
    }

    componentDidUpdate() {
        this.scrollToBottom();
    }

    render() {
        return <Dialog title="Provision Progress" className="provision-dialog" isOpen={this.props.isOpen}
                       onOpen={this.onOpen} disableClose={this.state.disableClose} onClose={this.onClose}>
                   <div className="progress-bar">
                       <div style={{ width: `${this.state.percent}%` }}></div></div>
                   <div className="messages scroller" ref={this._scrollerRef}>
                       <ul>
                           {this.state.messages.map(
                               (message, i) => <li key={i} className={message.startsWith(" ")
                                   ? "indent"
                                   : message.startsWith("*")
                                   ? "error"
                                   : ""}>{message.startsWith("*") ? message.substring(1) : message}</li>)}
                       </ul>
                   </div>
                   <footer>
                       <button className="primary" disabled={this.state.disableClose}
                               onClick={this.onClose}>Close</button></footer>
               </Dialog>;
    }
}